import { Component } from '@ribajs/core'
import { EventDispatcher } from '@ribajs/events'
import { debounce } from '@ribajs/utils'

/**
 * Ported from original theme code to Riba
 */
export class PredictiveSearch extends Component {
  public static tagName = 'predictive-search'

  /** Listen for client side routing events */
  protected routerEvents = new EventDispatcher('main')

  cachedResults: {
    [queryKey: string]: string
  } = {}

  input?: HTMLInputElement
  predictiveSearchResults?: HTMLDivElement
  statusElement?: HTMLDivElement

  isOpen = false
  loadingText = ''
  resultsMaxHeight = 0

  onScrollHandler = debounce(this.onScroll.bind(this))
  onChangeHandler = debounce(this._onChange.bind(this))
  onFocusHandler = this._onFocus.bind(this)
  onFocusOutHandler = this._onFocusOut.bind(this)
  onKeyupHandler = this._onKeyup.bind(this)
  onKeydownHandler = this._onKeydown.bind(this)
  onFormSubmitHandler = this._onFormSubmit.bind(this)

  scope = {}

  static get observedAttributes(): string[] {
    return []
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.init(PredictiveSearch.observedAttributes)

    this.input =
      this.querySelector<HTMLInputElement>('input[type="search"]') || undefined
    this.predictiveSearchResults =
      this.querySelector<HTMLDivElement>('[data-predictive-search]') ||
      undefined

    this.addEventListeners()
  }

  async afterBind() {
    await super.afterBind()
    this.classList.add(`${PredictiveSearch.tagName}-ready`)
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  protected onNewPageReady() {
    this.close()
  }

  addEventListeners() {
    const form = this.querySelector<HTMLFormElement>('form.search')

    if (!form) {
      throw new Error('Form element not found!')
    }

    form.addEventListener('submit', this.onFormSubmitHandler)

    this.input?.addEventListener('input', this.onChangeHandler)
    this.input?.addEventListener('focus', this.onFocusHandler)
    this.addEventListener('focusout', this.onFocusOutHandler)
    this.addEventListener('keyup', this.onKeyupHandler)
    this.addEventListener('keydown', this.onKeydownHandler)

    window.addEventListener('scroll', this.onScrollHandler, { passive: true })
    window.addEventListener('resize', this.onScrollHandler, { passive: true })

    this.routerEvents.on('newPageReady', this.onNewPageReady, this)
  }

  removeEventListeners() {
    const form = this.querySelector<HTMLFormElement>('form.search')
    form?.removeEventListener('submit', this.onFormSubmitHandler)

    this.input?.removeEventListener('input', this.onChangeHandler)
    this.input?.removeEventListener('focus', this.onFocusHandler)
    this.removeEventListener('focusout', this.onFocusOutHandler)
    this.removeEventListener('keyup', this.onKeyupHandler)
    this.removeEventListener('keydown', this.onKeydownHandler)

    window.removeEventListener('scroll', this.onScrollHandler)
    window.removeEventListener('resize', this.onScrollHandler)

    this.routerEvents.off('newPageReady', this.onNewPageReady, this)
  }

  getQuery() {
    return this.input?.value?.trim()
  }

  private _onChange() {
    const searchTerm = this.getQuery()

    if (!searchTerm?.length) {
      this.close(true)
      return
    }

    this.getSearchResults(searchTerm)
  }

  private _onFormSubmit(event: Event) {
    if (
      !this.getQuery()?.length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault()
  }

  private _onFocus() {
    const searchTerm = this.getQuery()

    if (!searchTerm?.length) return

    if (this.getAttribute('results') === 'true') {
      this.open()
    } else {
      this.getSearchResults(searchTerm)
    }
  }

  private _onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close()
    })
  }

  private _onKeyup(event: KeyboardEvent) {
    if (!this.getQuery()?.length) this.close(true)
    event.preventDefault()

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break
      case 'ArrowDown':
        this.switchOption('down')
        break
      case 'Enter':
        this.selectOption()
        break
      case 'Escape':
        this.close(true)
        break
    }
  }

  private _onKeydown(event: KeyboardEvent) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault()
    }
  }

  switchOption(direction: 'up' | 'down') {
    if (!this.getAttribute('open')) return

    const moveUp = direction === 'up'
    const selectedElement = this.querySelector('[aria-selected="true"]')
    const allElements = this.querySelectorAll('li')
    let activeElement: HTMLLIElement | undefined =
      this.querySelector('li') || undefined

    if (moveUp && !selectedElement) return

    if (this.statusElement) this.statusElement.textContent = ''

    if (!moveUp && selectedElement) {
      activeElement = (selectedElement.nextElementSibling || allElements[0]) as
        | HTMLLIElement
        | undefined
    } else if (moveUp) {
      activeElement = (selectedElement?.previousElementSibling ||
        allElements[allElements.length - 1]) as HTMLLIElement | undefined
    }

    if (activeElement === selectedElement) return

    activeElement?.setAttribute('aria-selected', 'true')
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false')

    this.setLiveRegionText(activeElement?.textContent || '')
    this.input?.setAttribute('aria-activedescendant', activeElement?.id || '')
  }

  selectOption() {
    const selectedProduct = this.querySelector<
      HTMLAnchorElement | HTMLButtonElement
    >('[aria-selected="true"] a, [aria-selected="true"] button')

    if (selectedProduct) selectedProduct.click()
  }

  getSearchResults(searchTerm: string) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase()
    this.setLiveRegionLoadingState()

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    fetch(
      `${window.routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm,
      )}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent(
        'resources[limit]',
      )}=4&section_id=predictive-search`,
    )
      .then(response => {
        if (!response.ok) {
          const error = new Error(response.status.toString())
          this.close()
          throw error
        }

        return response.text()
      })
      .then(text => {
        const resultsMarkup =
          new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search')?.innerHTML ||
          ''
        this.cachedResults[queryKey] = resultsMarkup
        this.renderSearchResults(resultsMarkup)
      })
      .catch(error => {
        this.close()
        throw error
      })
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector<HTMLDivElement>('.predictive-search-status') ||
      undefined
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text') || ''

    this.setLiveRegionText(this.loadingText)
    this.setAttribute('loading', 'true')
  }

  setLiveRegionText(statusText: string) {
    if (!this.statusElement) return
    this.statusElement.setAttribute('aria-hidden', 'false')
    this.statusElement.textContent = statusText

    setTimeout(() => {
      if (!this.statusElement) return
      this.statusElement.setAttribute('aria-hidden', 'true')
    }, 1000)
  }

  renderSearchResults(resultsMarkup: string) {
    if (this.predictiveSearchResults)
      this.predictiveSearchResults.innerHTML = resultsMarkup
    this.setAttribute('results', 'true')

    this.setLiveRegionResults()
    this.open()
  }

  setLiveRegionResults() {
    this.removeAttribute('loading')
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        ?.textContent || '',
    )
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      (document
        .getElementById('shopify-section-header')
        ?.getBoundingClientRect().bottom || 0)
    return this.resultsMaxHeight
  }

  setResultsStyle() {
    if (!this.predictiveSearchResults || !this.input) return

    this.predictiveSearchResults.style.maxHeight = (
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`
    ).toString()
    this.predictiveSearchResults.style.top =
      this.input.getBoundingClientRect().top +
      this.input.clientHeight +
      1 +
      'px'
    this.predictiveSearchResults.style.width = this.input.clientWidth + 'px'
  }

  onScroll() {
    if (this.isOpen && this.predictiveSearchResults) {
      this.setResultsStyle()
    }
  }

  open() {
    if (!this.predictiveSearchResults) {
      console.warn('Search results container not found!')
      return
    }
    this.setResultsStyle()
    this.setAttribute('open', 'true')
    this.input?.setAttribute('aria-expanded', 'true')
    this.isOpen = true
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = ''
      this.removeAttribute('results')
    }

    const selected = this.querySelector('[aria-selected="true"]')

    if (selected) selected.setAttribute('aria-selected', 'false')

    this.input?.setAttribute('aria-activedescendant', '')
    this.removeAttribute('open')
    this.input?.setAttribute('aria-expanded', 'false')
    this.resultsMaxHeight = 0
    this.predictiveSearchResults?.removeAttribute('style')

    this.isOpen = false
  }

  protected template() {
    return null
  }
}
